import { useState, useRef, useEffect } from 'react'
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { app } from '../../firebase'
import './ImageUploader.css'
import PhotoCamera from '@mui/icons-material/PhotoCamera'
import Folder from '@mui/icons-material/Folder'
import Close from '@mui/icons-material/Close'
import CircularProgress from '@mui/icons-material/Autorenew'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import {
  getImageReferenceName,
  generateUniqueReferenceName,
  normalizeUmlauts
} from '../../utils/imageMentions'

function ImageUploader ({
  images = [],
  onChange,
  maxImages = 10,
  maxSizeMB = 5,
  disabled = false,
  companyId = null, // Required for immediate upload
  onImageNameChange = null // Callback: (imageId, oldReferenceName, newReferenceName, newName)
}) {
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const [editingImageId, setEditingImageId] = useState(null)
  const [editingValue, setEditingValue] = useState('')
  const [editingError, setEditingError] = useState(null)
  const fileInputRef = useRef(null)
  const nameInputRef = useRef(null)

  const validateFile = file => {
    const validTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/webp',  // Images
      'video/mp4', 'video/webm', 'video/quicktime',           // Videos (MP4, WebM, MOV)
      'application/pdf'                                       // PDFs
    ]
    if (!validTypes.includes(file.type)) {
      return `${file.name}: Nur JPG, PNG, WebP Bilder, MP4, WebM, MOV Videos oder PDFs erlaubt`
    }

    // Different size limits for videos, PDFs vs images
    const isVideo = file.type.startsWith('video/')
    const isPDF = file.type === 'application/pdf'
    const maxSizeBytes = isVideo ? 100 * 1024 * 1024 : (isPDF ? 50 * 1024 * 1024 : maxSizeMB * 1024 * 1024) // 100MB for videos, 50MB for PDFs, 5MB for images
    const maxSizeLabel = isVideo ? '100MB' : (isPDF ? '50MB' : `${maxSizeMB}MB`)

    if (file.size > maxSizeBytes) {
      return `${file.name}: Datei zu groß (max ${maxSizeLabel})`
    }

    return null
  }

  const handleFiles = async fileList => {
    setError(null)

    const filesArray = Array.from(fileList)

    if (images.length + filesArray.length > maxImages) {
      setError(`Maximal ${maxImages} Bilder erlaubt`)
      return
    }

    const errors = []
    const validFiles = []

    for (const file of filesArray) {
      const error = validateFile(file)
      if (error) {
        errors.push(error)
      } else {
        validFiles.push(file)
      }
    }

    if (errors.length > 0) {
      setError(errors.join(', '))
      return
    }

    // If companyId is provided, upload immediately to Firebase Storage
    if (companyId) {
      setUploading(true)
      setUploadProgress(`Lade ${validFiles.length} Datei(en) hoch...`)

      try {
        const storage = getStorage(app)
        const uploadedImages = []

        // Get existing reference names for uniqueness check
        const existingReferenceNames = images
          .map(img => img.referenceName || getImageReferenceName(img.name || img.filename || ''))
          .filter(Boolean)

        for (let i = 0; i < validFiles.length; i++) {
          const file = validFiles[i]
          setUploadProgress(`Lade Datei ${i + 1} von ${validFiles.length}...`)

          // Normalize filename (convert umlauts to ASCII)
          const normalizedFileName = normalizeUmlauts(file.name)

          // Create unique filename with timestamp
          const timestamp = Date.now()
          const uniqueId = Math.random().toString(36).substring(2, 9)
          const fileName = `${timestamp}_${uniqueId}_${normalizedFileName}`

          // Upload to knowledge-images path (unified path for drafts and knowledge)
          const storageRef = ref(
            storage,
            `knowledge-images/${companyId}/${fileName}`
          )

          // Upload with explicit metadata
          await uploadBytes(storageRef, file, {
            contentType: file.type || 'image/jpeg'
          })

          // Get download URL
          const url = await getDownloadURL(storageRef)

          // Generate unique reference name (use normalized filename)
          const baseRefName = getImageReferenceName(normalizedFileName)
          const referenceName = generateUniqueReferenceName(
            baseRefName,
            existingReferenceNames
          )
          existingReferenceNames.push(referenceName)

          // Detect media type (image, video, or pdf)
          let mediaType = 'image'
          if (file.type.startsWith('video/')) {
            mediaType = 'video'
          } else if (file.type === 'application/pdf') {
            mediaType = 'pdf'
          }

            uploadedImages.push({
              url,
              preview: url, // Use URL as preview (already uploaded)
              name: normalizedFileName, // Use normalized filename
              filename: normalizedFileName, // Use normalized filename
            size: file.size,
            id: `${timestamp}-${uniqueId}`,
            uploadedAt: new Date().toISOString(),
            storagePath: `knowledge-images/${companyId}/${fileName}`,
            referenceName, // Add reference name for @mention support
            mediaType // Add media type (image, video, or pdf)
          })
        }

        // Add uploaded images to existing images
        onChange([...images, ...uploadedImages])
        setUploadProgress('')
      } catch (err) {
        console.error('Image upload error:', err)
        setError('Fehler beim Hochladen: ' + err.message)
      } finally {
        setUploading(false)
        setUploadProgress('')
      }
    } else {
      // Fallback: No immediate upload (legacy behavior)
      // Images will only have preview (Base64) and file object
      
      // Get existing reference names for uniqueness check
      const existingReferenceNames = images
        .map(img => img.referenceName || getImageReferenceName(img.name || img.filename || ''))
        .filter(Boolean)
      
      const newImages = await Promise.all(
        validFiles.map(async file => {
          return new Promise(resolve => {
            // Normalize filename (convert umlauts to ASCII)
            const normalizedFileName = normalizeUmlauts(file.name)

            // Detect media type (image, video, or pdf)
            let mediaType = 'image'
            if (file.type.startsWith('video/')) {
              mediaType = 'video'
            } else if (file.type === 'application/pdf') {
              mediaType = 'pdf'
            }

            // PDFs and videos can't be read as DataURL for preview, so use a placeholder
            if (file.type === 'application/pdf' || file.type.startsWith('video/')) {
              // For PDFs and videos, use a placeholder URL (will be replaced after upload)
              const baseRefName = getImageReferenceName(normalizedFileName)
              const referenceName = generateUniqueReferenceName(
                baseRefName,
                existingReferenceNames
              )
              existingReferenceNames.push(referenceName)

              resolve({
                file,
                preview: '', // No preview for PDFs/videos in legacy mode
                name: normalizedFileName,
                filename: normalizedFileName,
                size: file.size,
                id: `${Date.now()}-${Math.random()}`,
                referenceName,
                mediaType
              })
              return
            }

            const reader = new FileReader()
            reader.onload = e => {
              // Generate unique reference name
              const baseRefName = getImageReferenceName(normalizedFileName)
              const referenceName = generateUniqueReferenceName(
                baseRefName,
                existingReferenceNames
              )
              existingReferenceNames.push(referenceName)

              resolve({
                file,
                preview: e.target.result,
                name: normalizedFileName, // Use normalized filename
                filename: normalizedFileName, // Also set filename property
                size: file.size,
                id: `${Date.now()}-${Math.random()}`,
                referenceName, // Add reference name for @mention support
                mediaType // Add media type (image, video, or pdf)
              })
            }
            reader.readAsDataURL(file)
          })
        })
      )

      onChange([...images, ...newImages])
    }
  }

  const handleDrag = e => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = e => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (disabled || uploading) return

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }

  const handleFileInputChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files)
    }
  }

  const handleRemoveImage = async imageId => {
    // Find the image to be removed
    const imageToRemove = images.find(img => img.id === imageId)
    
    // If image has a storagePath, delete it from Firestore Storage
    if (imageToRemove?.storagePath) {
      try {
        const storage = getStorage(app)
        const imageRef = ref(storage, imageToRemove.storagePath)
        await deleteObject(imageRef)
        console.log('Image deleted from Storage:', imageToRemove.storagePath)
      } catch (error) {
        // Log error but continue with removing from UI
        // This prevents UI blocking if deletion fails (e.g., permission issues)
        console.error('Error deleting image from Storage:', error)
        // Still remove from UI even if Storage deletion fails
      }
    }
    
    // Remove image from the array
    onChange(images.filter(img => img.id !== imageId))
  }

  const formatFileSize = bytes => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  // Focus input when editing starts
  useEffect(() => {
    if (editingImageId && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [editingImageId])

  const handleStartEditing = (imageId, currentName) => {
    if (disabled || uploading) return
    setEditingImageId(imageId)
    setEditingValue(currentName)
    setEditingError(null)
  }

  const handleCancelEditing = () => {
    setEditingImageId(null)
    setEditingValue('')
    setEditingError(null)
  }

  const handleSaveName = (imageId) => {
    if (!editingValue.trim()) {
      setEditingError('Name darf nicht leer sein')
      return
    }

    const image = images.find(img => img.id === imageId)
    if (!image) {
      setEditingError('Bild nicht gefunden')
      return
    }

    // Normalize the new name
    const normalizedName = normalizeUmlauts(editingValue.trim())
    
    // Generate reference name from the normalized name
    // Remove file extension if present
    const nameWithoutExt = normalizedName.replace(/\.[^/.]+$/, '')
    const baseRefName = normalizeUmlauts(nameWithoutExt)
    
    // Get existing reference names (excluding the current image)
    const existingReferenceNames = images
      .filter(img => img.id !== imageId)
      .map(img => img.referenceName || getImageReferenceName(img.name || img.filename || ''))
      .filter(Boolean)
    
    // Check if the new reference name would be unique
    const newReferenceName = generateUniqueReferenceName(baseRefName, existingReferenceNames)
    
    // If a suffix was added, show a warning but still allow it
    if (newReferenceName !== baseRefName) {
      setEditingError(`Name bereits vorhanden. Wird als "${newReferenceName}" gespeichert.`)
      // Still allow saving, but show the warning
    }

    // Get old reference name
    const oldReferenceName = image.referenceName || getImageReferenceName(image.name || image.filename || '')

    // Update the image object
    const updatedImages = images.map(img => {
      if (img.id === imageId) {
        return {
          ...img,
          name: normalizedName,
          filename: normalizedName,
          referenceName: newReferenceName
        }
      }
      return img
    })

    // Update images array
    onChange(updatedImages)

    // Call callback if provided
    if (onImageNameChange) {
      onImageNameChange(imageId, oldReferenceName, newReferenceName, normalizedName)
    }

    // Clear editing state
    setEditingImageId(null)
    setEditingValue('')
    setEditingError(null)
  }

  const handleNameKeyDown = (e, imageId) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveName(imageId)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelEditing()
    }
  }

  return (
    <div className='image-uploader'>
      <div
        className={`upload-zone ${dragActive ? 'drag-active' : ''} ${
          disabled || uploading ? 'disabled' : ''
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept='image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/webm,video/quicktime,application/pdf'
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled || uploading}
        />

        <div className='upload-zone-content'>
          {uploading ? (
            <>
              <CircularProgress className='upload-icon spinning' />
              <p className='upload-text'>{uploadProgress}</p>
            </>
          ) : (
            <>
              <PhotoCamera className='upload-icon' />
              <p className='upload-text'>
                {dragActive
                  ? 'Bilder/Videos/PDFs hier ablegen'
                  : 'Bilder, Videos oder PDFs hier ablegen oder klicken zum Auswählen'}
              </p>
              <button
                type='button'
                className='btn-upload'
                onClick={e => {
                  e.stopPropagation()
                  !disabled && !uploading && fileInputRef.current?.click()
                }}
                disabled={disabled || uploading}
              >
                <Folder style={{ marginRight: '8px' }} />
                Durchsuchen
              </button>
            </>
          )}
        </div>
      </div>

      <div className='upload-info'>
        Max {maxImages} Dateien • Bilder (JPG, PNG, WebP, max 5MB) • Videos (MP4, WebM, MOV, max 100MB) • PDFs (max 50MB)
        {images.length > 0 && (
          <span className='image-count'>
            {' '}
            • {images.length}/{maxImages} hochgeladen
          </span>
        )}
      </div>

      {error && <div className='upload-error'>{error}</div>}

      {images.length > 0 && (
        <div className='image-preview-grid'>
          {images.map(image => {
            const isVideo = image.mediaType === 'video' || (image.preview || image.url || '').startsWith('data:video/') || (image.filename || '').match(/\.(mp4|webm|mov)$/i)
            const isPDF = image.mediaType === 'pdf' || (image.filename || image.name || '').match(/\.pdf$/i)
            return (
            <div key={image.id} className='image-preview-wrapper'>
              <div className='image-preview-item'>
                {isPDF ? (
                  <div style={{ 
                    width: '100%', 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: '#f5f5f5',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <PictureAsPdf style={{ fontSize: '48px', color: '#d32f2f' }} />
                    <span style={{ fontSize: '12px', color: '#666' }}>PDF</span>
                  </div>
                ) : isVideo ? (
                  <video
                    src={image.preview || image.url}
                    controls
                    preload="metadata"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <img src={image.preview || image.url} alt={image.name} />
                )}
                <div className='image-preview-overlay'>
                  <button
                    type='button'
                    className='btn-remove-image-uploader'
                    onClick={() => handleRemoveImage(image.id)}
                    disabled={disabled || uploading}
                    title={isPDF ? 'PDF entfernen' : (isVideo ? 'Video entfernen' : 'Bild entfernen')}
                  >
                    <Close />
                  </button>
                </div>
              </div>
              {editingImageId === image.id ? (
                <div className='image-filename-edit-wrapper'>
                  <input
                    ref={nameInputRef}
                    type='text'
                    className='image-filename-input'
                    value={editingValue}
                    onChange={e => {
                      setEditingValue(e.target.value)
                      setEditingError(null)
                    }}
                    onKeyDown={e => handleNameKeyDown(e, image.id)}
                    onBlur={() => handleSaveName(image.id)}
                    disabled={disabled || uploading}
                    title='Enter zum Speichern, Escape zum Abbrechen'
                  />
                  {editingError && (
                    <div className='image-filename-error'>{editingError}</div>
                  )}
                </div>
              ) : (
                <div
                  className={`image-filename-label ${!disabled && !uploading ? 'editable' : ''}`}
                  title={disabled || uploading ? image.name : `${image.name} (Klicken zum Bearbeiten)`}
                  onClick={() => handleStartEditing(image.id, image.name)}
                >
                  {image.name}
                </div>
              )}
            </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ImageUploader
